import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import ReactPlayer from 'react-player/lazy';
import Image from '../Image';
import Intersect from '../Intersect';
import deviceInfo from '../../helpers/deviceInfo';

import './styles.scss';

const CardMedia = ({
  url,
  title = '',
  mimeType,
  mediaContainerClasses = '',
  mediaClasses,
  imageFit,
  isAutoplay,
  loop,
  muted = true,
  playing = true,
  cardImageSizes,
  playOnIntersection = false,
  controls = false,
  attributes = {},
  onClick,
  onVideoIntersection
}) => {
  const playerRef = useRef();
  const intersectRef = useRef();
  const mediaType = mimeType.includes('video') ? 'video' : 'image';
  const cardMediaClasses = classNames(
    'card-media',
    {
      [`card-media--${imageFit}`]: imageFit
    },
    mediaContainerClasses
  );

  let progressArr = [];

  // gtm tags for video player
  const onPlay = () => {
    dataLayer.push({
      event: 'video',
      eventCategory: 'html5 video',
      eventAction: 'video play',
      eventLabel: title
    });
  };

  const onPause = () => {
    dataLayer.push({
      event: 'video',
      eventCategory: 'html5 video',
      eventAction: 'video pause',
      eventLabel: title
    });
  };

  const onProgress = data => {
    if (data.played.toFixed(2) == '0.25' && !progressArr.includes('0.25')) {
      progressArr.push('0.25');
      dataLayer.push({
        event: 'video',
        eventCategory: 'html5 video',
        eventAction: 'video_progress_25',
        eventLabel: title
      });
    }

    if (data.played.toFixed(2) == '0.50' && !progressArr.includes('0.50')) {
      progressArr.push('0.50');
      dataLayer.push({
        event: 'video',
        eventCategory: 'html5 video',
        eventAction: 'video_progress_50',
        eventLabel: title
      });
    }

    if (data.played.toFixed(2) == '0.75' && !progressArr.includes('0.75')) {
      progressArr.push('0.75');
      dataLayer.push({
        event: 'video',
        eventCategory: 'html5 video',
        eventAction: 'video_progress_75',
        eventLabel: title
      });
    }

    if (data.played.toFixed(2) == '0.90' && !progressArr.includes('0.90')) {
      progressArr.push('0.90');
      dataLayer.push({
        event: 'video',
        eventCategory: 'html5 video',
        eventAction: 'video_progress_90',
        eventLabel: title
      });
    }

    // video is on a loop, we're tracking when
    // duration of the video finishes instead of ended
    if (data.played.toFixed(2) == '0.98' && !progressArr.includes('0.98')) {
      progressArr.push('0.98');
      dataLayer.push({
        event: 'video',
        eventCategory: 'html5 video',
        eventAction: 'video_progress_completion',
        eventLabel: title
      });
    }
  };

  function onSeekHandler() {
    progressArr = [];
  }

  function onElementIntersection(e) {
    if (!playOnIntersection || !playerRef.current) return;
    const player = playerRef.current.getInternalPlayer();
    if (e.intersectionRatio >= 0.25) {
      player.play();
      onVideoIntersection(true);
    } else {
      player.pause();
      onVideoIntersection(false);
    }
  }

  function onCanPlay() {
    const player = playerRef.current.getInternalPlayer();

    player.removeEventListener('canplaythrough', onCanPlay, false);
    player.removeEventListener('load', onCanPlay, false);
    player.play();
  }

  function onReadyHandler() {
    if (playing && playerRef.current && playerRef.current.getInternalPlayer()) {
      const player = playerRef.current.getInternalPlayer();

      player.play();

      if (player.readyState !== 4) {
        player.addEventListener('canplaythrough', onCanPlay, false);
        player.addEventListener('load', onCanPlay, false);

        setTimeout(() => {
          player.pause();
        }, 1);
      }
    }
  }

  useEffect(() => {
    if (deviceInfo.isIOS && playerRef.current && playerRef.current.getInternalPlayer()) {
      const player = playerRef.current.getInternalPlayer();

      player.load();
    }
  }, []);

  return (
    <div className={cardMediaClasses} ref={intersectRef}>
      {mediaType === 'image' ? (
        <Image
          classes={mediaClasses || ''}
          image={url}
          alt={title}
          sizes={cardImageSizes}
          onClick={onClick}
        />
      ) : (
        <Intersect
          active={playOnIntersection}
          config={{
            root: null,
            margin: '0px',
            threshold: [0.25]
          }}
          onIntersection={entry => onElementIntersection(entry[0])}
          elementRef={intersectRef}>
          <ReactPlayer
            ref={playerRef}
            className={mediaClasses || ''}
            url={url}
            controls={controls}
            autoPlay={isAutoplay || false}
            playing={playing}
            onProgress={onProgress}
            onPlay={onPlay}
            onSeek={() => {
              onSeekHandler();
            }}
            onPause={onPause}
            loop={loop || false}
            width="100%"
            height="100%"
            playsinline={true}
            muted={muted || false}
            volume={muted ? 0 : 1}
            config={{
              file: {
                attributes: {
                  autoPlay: false,
                  style: {
                    objectFit: imageFit === 'fit' ? 'contain' : 'cover',
                    width: '100%',
                    height: '100%'
                  },
                  ...attributes
                }
              }
            }}
            onReady={() => {
              onReadyHandler();
            }}
            title={title}
            onClick={onClick}
          />
        </Intersect>
      )}
    </div>
  );
};

export default CardMedia;
